import React, { useState } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  CircularProgress,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import format from "date-fns/format";
import subMonths from "date-fns/subMonths";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const DownloadStatementModal = ({ isOpen, setModal }) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { success, snackError } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [accountNumber, setAccountNumber] = useState("");
  const [fromDate, setFromDate] = useState(format(subMonths(new Date(), 1), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isPdf, setIsPdf] = useState("true");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setModal(false);
    setIsLoading(false);
  };

  const base64ToBlob = (base64, type = "application/pdf") => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: type });
  };

  const convertToCSV = (data) => {
    const headers = [
      "Serial Number", "Transaction Date", "Transaction ID", "Reference",
      "Type", "Nature", "Amount", "Fee", "Narration",
      "From Account", "To Account", "Payer Name", "Receiver Name", "Status", "Balance"
    ];

    const rows = (data.transactions || []).map(tx => [
      tx.serialNumber || "",
      tx.transactionDate || "",
      tx.transactionId || "",
      tx.reference || "",
      tx.transactionType || "",
      tx.transactionNature || "",
      tx.amount || 0,
      tx.fee !== null && tx.fee !== undefined ? tx.fee : "",
      tx.narration ? `"${tx.narration.replace(/"/g, '""')}"` : "",
      tx.fromAccount || "",
      tx.toAccount || "",
      tx.payerName ? `"${tx.payerName.replace(/"/g, '""')}"` : "",
      tx.receiverName ? `"${tx.receiverName.replace(/"/g, '""')}"` : "",
      tx.status || "",
      tx.balance !== null && tx.balance !== undefined ? tx.balance : ""
    ]);

    const summary = [
      ["Account Number", data.accountNumber || ""],
      ["Statement Period", data.statementPeriod?.formattedPeriod || ""],
      ["Opening Balance", data.openingBalance || 0],
      ["Closing Balance", data.closingBalance || 0],
      ["Total Debits", data.totalDebits || 0],
      ["Total Credits", data.totalCredits || 0],
      ["Transaction Count", data.transactionCount || 0],
      [],
      headers
    ];

    return [...summary, ...rows].map(e => e.join(",")).join("\n");
  };

  const generatePDFStatement = (data) => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // WayaBank Header
    doc.setFontSize(22);
    doc.setTextColor(255, 103, 0); // #ff6700
    doc.text("WayaBank", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Official Account Statement", 14, 25);

    // Account Summary Info
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Account Number: ${data.accountNumber || ""}`, 14, 35);
    doc.text(`Statement Period: ${data.statementPeriod?.formattedPeriod || ""}`, 14, 40);

    const summaryData = [
      ["Opening Balance", `NGN ${data.openingBalance || 0}`],
      ["Closing Balance", `NGN ${data.closingBalance || 0}`],
      ["Total Debits", `NGN ${data.totalDebits || 0}`],
      ["Total Credits", `NGN ${data.totalCredits || 0}`],
      ["Transaction Count", `${data.transactionCount || 0}`]
    ];

    doc.autoTable({
      startY: 45,
      head: [["Summary Metric", "Value"]],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [255, 103, 0] },
      margin: { left: 14, right: 14 }
    });

    // Transactions Table
    const tableColumn = ["S/N", "Date", "Transaction ID", "Reference", "Type", "Nature", "Amount", "Fee", "Status"];
    const tableRows = (data.transactions || []).map(tx => {
      let formattedDate = "";
      if (tx.transactionDate) {
        try {
          formattedDate = format(new Date(tx.transactionDate), 'yyyy-MM-dd HH:mm:ss');
        } catch (e) {
          formattedDate = tx.transactionDate;
        }
      }
      return [
        tx.serialNumber || "",
        formattedDate,
        tx.transactionId || "",
        tx.reference || "",
        tx.transactionType || "",
        tx.transactionNature || "",
        tx.amount || 0,
        tx.fee !== null && tx.fee !== undefined ? tx.fee : 0,
        tx.status || ""
      ];
    });

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [0, 21, 41] }, // Dark header
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });

    doc.save(`Statement_${data.accountNumber || "Account"}_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!accountNumber || !fromDate || !toDate) {
      snackError("Please fill all fields");
      return;
    }

    setIsLoading(true);
    const pdfFlag = isPdf === "true";
    const params = new URLSearchParams({
      fromDate,
      toDate,
      isPdf: pdfFlag
    });

    try {
      const response = await axiosPrivate.get(
        `/transaction-payment/api/v1/admin/account/${accountNumber}/statement?${params.toString()}`
      );
      
      const responseData = response?.data?.data || response?.data;
      if (responseData) {
        if (pdfFlag) {
          if (responseData.pdfContent) {
            const blob = base64ToBlob(responseData.pdfContent, 'application/pdf');
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Statement_${accountNumber}_${format(new Date(), 'yyyyMMdd')}.pdf`;
            link.click();
            success("Statement generated and downloaded successfully");
          } else if (responseData.transactions) {
            generatePDFStatement(responseData);
            success("Statement generated and downloaded successfully");
          } else {
            snackError("No statement content found in response");
          }
        } else {
          if (responseData.transactions) {
            const csvContent = convertToCSV(responseData);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Statement_${accountNumber}_${format(new Date(), 'yyyyMMdd')}.csv`;
            link.click();
            success("Statement generated and downloaded successfully");
          } else {
            snackError("No transaction data found in statement");
          }
        }
      } else {
        snackError("No statement data found");
      }
      handleClose();
    } catch (error) {
      console.error(error);
      snackError(error?.response?.data?.message || "Failed to generate statement");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          width: isNonMobile ? "400px" : "90%",
          backgroundColor: colors.primary[400],
          p: 4,
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          boxShadow: 24,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <form onSubmit={handleFormSubmit}>
          <Box display="flex" flexDirection="column" gap="20px">
            <Typography variant="h3" sx={{ textAlign: "center", mb: 1, fontWeight: "bold" }}>
              Download Statement of Account
            </Typography>
            
            <TextField
              fullWidth
              variant="outlined"
              type="text"
              label="Account Number"
              onChange={(e) => setAccountNumber(e.target.value)}
              value={accountNumber}
              name="accountNumber"
              required
            />

            <TextField
              fullWidth
              variant="outlined"
              type="date"
              label="From Date"
              onChange={(e) => setFromDate(e.target.value)}
              value={fromDate}
              name="fromDate"
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              fullWidth
              variant="outlined"
              type="date"
              label="To Date"
              onChange={(e) => setToDate(e.target.value)}
              value={toDate}
              name="toDate"
              InputLabelProps={{ shrink: true }}
              required
            />

            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ color: colors.grey[100] }}>Format</FormLabel>
              <RadioGroup
                row
                aria-label="format"
                name="isPdf"
                value={isPdf}
                onChange={(e) => setIsPdf(e.target.value)}
              >
                <FormControlLabel value="true" control={<Radio color="secondary" />} label="PDF" />
                <FormControlLabel value="false" control={<Radio color="secondary" />} label="CSV" />
              </RadioGroup>
            </FormControl>

            <Button
              type="submit"
              color="secondary"
              variant="contained"
              disabled={isLoading}
              sx={{ width: "100%", p: 1.5, mt: 1, fontWeight: "bold" }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Download"}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default DownloadStatementModal;
